/** DocuSeal on-prem embed helpers (builder.js / form.js must load from your instance, not CDN). */

export type DocuSealEmbedScript = 'builder' | 'form'

const CUSTOM_ELEMENT: Record<DocuSealEmbedScript, string> = {
    builder: 'docuseal-builder',
    form: 'docuseal-form',
}

const SCRIPT_PATH: Record<DocuSealEmbedScript, string> = {
    builder: '/js/builder.js',
    form: '/js/form.js',
}

const scriptLoadPromises = new Map<string, Promise<void>>()

/** Domain only, e.g. sign.ezturn.co — used for data-host and script URLs. */
export function normalizeDocuSealHost(host: string): string {
    return host.replace(/^https?:\/\//, '').replace(/\/+$/, '')
}

export function docuSealScriptUrl(
    host: string,
    script: DocuSealEmbedScript,
): string {
    const domain = normalizeDocuSealHost(host)
    return `https://${domain}${SCRIPT_PATH[script]}`
}

export function loadDocuSealScript(
    host: string,
    script: DocuSealEmbedScript,
): Promise<void> {
    const domain = normalizeDocuSealHost(host)
    if (!domain) {
        return Promise.reject(new Error('DocuSeal host is not configured'))
    }

    const elementName = CUSTOM_ELEMENT[script]
    if (typeof window !== 'undefined' && customElements.get(elementName)) {
        return Promise.resolve()
    }

    const scriptUrl = docuSealScriptUrl(domain, script)
    const cached = scriptLoadPromises.get(scriptUrl)
    if (cached) {
        return cached
    }

    const promise = new Promise<void>((resolve, reject) => {
        const existing = document.querySelector(`script[src="${scriptUrl}"]`)
        if (existing) {
            existing.addEventListener('load', () => resolve())
            existing.addEventListener('error', () =>
                reject(new Error(`Failed to load DocuSeal ${script} script`)),
            )
            return
        }
        const tag = document.createElement('script')
        tag.src = scriptUrl
        tag.async = true
        tag.onload = () => resolve()
        tag.onerror = () =>
            reject(new Error(`Failed to load DocuSeal ${script} script`))
        document.head.appendChild(tag)
    })

    scriptLoadPromises.set(scriptUrl, promise)
    return promise
}
